import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import styles from './style.module.scss'
import infoIcon from '@assets/images/icon/info-icon.svg'

import verified from '@assets/images/icon/verified_badge.png'
import { convertDt24hFormat, dateCheck, checkPageNumberInSlug, checkIsGlanceView, getAllImgs } from '@utils'
import { DEFAULT_BLOG_READ, IS_GLANCE_MLIB, WIDGET } from '@shared/constants'
import { allRoutes } from '@shared/constants/allRoutes'
import useWindowSize from '@shared/hooks/windowSize'
import useOnScreen from '@shared/hooks/useOnScreen'
import Layout from '@shared/components/layout'
import useArticleLikeComment from '@shared/hooks/useArticleLikeComment'
import Script from 'next/script'

const BreadcrumbNav = dynamic(() => import('@shared-components/breadcrumbNav'))
const ArticleActions = dynamic(() => import('@shared-components/articleDetail/articleActions'))
const LikeComment = dynamic(() => import('@shared-components/articleDetail/likeComment'))
const ArticleRelatedVideos = dynamic(() => import('@shared-components/articleDetail/articleRelatedVideos'), { ssr: false })
const CurrentSeries = dynamic(() => import('@shared/components/widgets/currentSeries'))
const AllWidget = dynamic(() => import('@shared/components/allWidget'), { ssr: false })
const TaboolaAds = dynamic(() => import('@shared/components/ads/taboola'), { ssr: false })
const MyImage = dynamic(() => import('@shared/components/myImage'))
const ResponsiveImg = dynamic(() => import('../responsiveImage'))
// const ViewIcon = dynamic(() => import('@shared-components/ctIcons/viewIcon'))
const ClockIcon = dynamic(() => import('@shared-components/ctIcons/clockIcon'))
const ExclusiveIcon = dynamic(() => import('@shared-components/ctIcons/exclusiveIcon'), { ssr: false })
const EditorsPickIcon = dynamic(() => import('@shared-components/ctIcons/editorsPickIcon'), { ssr: false })
const GlanceAd = dynamic(() => import('@shared-components/ads/glanceAd'), { ssr: false })
const RelatedArticle = dynamic(() => import('@shared-components/articleDetail/relatedArticles'), { ssr: false })
const CustomLink = dynamic(() => import('@shared/components/customLink'))
const OnMouseAndScroll = dynamic(() => import('@shared/components/lazyLoad/onMouseAndScroll'))
const ErrorBoundary = dynamic(() => import('@shared/components/errorBoundary'))
const ArticleStories = dynamic(() => import('@shared/components/stories/article-stories'))
// const VuuklePlugin = dynamic(() => import('@shared/components/vuukle-plugin'))
const MultiPoll = dynamic(() => import('@shared/components/multiPoll'))
const MultiQuiz = dynamic(() => import('@shared/components/multiQuiz'))
const Ads = dynamic(() => import('@shared/components/ads'))
const TapMindSlider = dynamic(() => import('@shared/components/ads/tapMind-slider'))
// const CricketPedia = dynamic(() => import('@shared/components/widgets/cricketPedia'))

function ArticleDetail({ article, isPreviewMode, seoData, children, type }) {
  const router = useRouter()
  const { t } = useTranslation()
  const [width] = useWindowSize()
  const { inView, observe } = useOnScreen()
  const mobileWebViewtheme = router?.query?.theme
  const isMobileWebView = router?.query?.isMobileWebView
  const isMobileWebViewV2 = router?.query?.isMobileWebViewV2
  const isGlanceView = checkIsGlanceView(router?.query)
  const [isPreview, setIsPreview] = useState(!!isMobileWebView)
  const { mainURL, largeSet, mediumSet } = getAllImgs(article?.oImg?.sUrl)
  const mediaSet = useRef([largeSet, mediumSet])

  const page = useMemo(() => {
    const slug = router?.asPath?.split('/').filter((e) => e)
    const { lastSlug } = checkPageNumberInSlug(slug)
    return lastSlug ? Number(lastSlug) || 1 : 1
  }, [router?.asPath])

  const mobileWebViewFontClass = useMemo(() => {
    if (isMobileWebView === 'true' && isMobileWebViewV2 === 'true') {
      return styles.luxendFont
    } else {
      return styles.notoFont
    }
  }, [isMobileWebView, isMobileWebViewV2])

  const { currentClap, setCurrentClap, totalClaps, commentCount, setCommentCount, showComments, setShowComments, handleAddClap } =
    useArticleLikeComment({ article, isPreview: isPreviewMode })
  // const token = getToken()

  useEffect(() => {
    if (isMobileWebView) {
      setIsPreview(true)
      document.body.classList.add('mobileWebView')
      if (mobileWebViewtheme === 'dark') {
        document.body.setAttribute('data-mode', 'dark')
      } else {
        document.body.setAttribute('data-mode', 'light')
      }
    }
    mediaSet.current = [largeSet, mediumSet]
  }, [router?.asPath])

  function refreshAds() {
    let interval
    if (isGlanceView && !IS_GLANCE_MLIB) {
      const SECONDS_TO_WAIT_AFTER_VIABILITY = 30
      import('@shared/libs/ads').then(({ refreshGoogleAds }) => {
        interval = setInterval(() => {
          refreshGoogleAds()
        }, SECONDS_TO_WAIT_AFTER_VIABILITY * 1000)
      })
    }
    return () => {
      clearInterval(interval)
    }
  }

  useEffect(() => {
    return refreshAds()
  }, [])

  function handleGoogleNews() {
    ;(self.SWG_BASIC = self.SWG_BASIC || []).push((basicSubscriptions) => {
      basicSubscriptions.init({
        type: 'NewsArticle',
        isPartOfType: ['Product'],
        isPartOfProductId: 'CAow_sf-Cg:openaccess',
        clientOptions: { theme: 'light', lang: 'en' }
      })
    })
  }

  return (
    <Layout data={{ ...article, oSeo: { ...article?.oSeo, ...seoData } }} isPreviewMode={isPreviewMode}>
      <main className={`${styles.articlePage} ${isPreview && styles.mobileArticle} ${mobileWebViewFontClass}`}>
        <div className={`${styles.articleBlockSec} common-section ${isPreview && 'pt-0 pt-sm-4'}`}>
          <div className="container">
            {/* {width < 768 && ( // Mcanvas ads for mobile only
              <Ads
                id="div-ad-desk-138639789-1662468567-0"
                adIdDesktop="Crictracker_OLD_Mcanvas_1x1"
                dimensionDesktop={[1, 1]}
                className={`p-0 ${styles?.mCanvasAd}`}
                hidePreview
              />
            )} */}
            {isGlanceView && (
              <GlanceAd
                id="div-gpt-ad-1"
                adId="Crictracker_mrec_top"
                dimension={[
                  [300, 250],
                  [336, 280]
                ]}
                className="mb-2 d-flex justify-content-center"
                adUnitName="Crictracker_English_InArticleMedium_Top"
                placementName="InArticleMedium"
                width={300}
                height={250}
                pageName="crictracker.com"
              />
            )}
            {!isPreviewMode && (
              <div className="d-none d-md-block mb-4" style={{ minHeight: '100px', marginTop: '-10px' }}>
                {width > 768 && ( // Desktop Top
                  <Ads
                    id="div-ad-gpt-23148549803-1728886625-0"
                    adIdDesktop="Crictracker_New_Desktop_AP_ATF1_728x90"
                    dimensionDesktop={[728, 90]}
                    slotId="23148549803"
                    className="text-center"
                  />
                )}
              </div>
            )}
            <div className="row justify-content-center justify-content-lg-start gx-3 gx-xxl-4">
              <div className={`col-lg-8 col-md-11 ${isPreviewMode ? '' : 'left-content'}`}>
                <div className={`${styles.articleBlock} common-box position-relative`}>
                  <div
                    className={`d-flex flex-wrap flex-md-nowrap align-items-start justify-content-between ${
                      article?.oAdvanceFeature?.bEditorsPick && article?.oAdvanceFeature?.bExclusiveArticle && 'flex-column flex-sm-row'
                    }`}
                  >
                    {!isPreviewMode && !isPreview && !isGlanceView && <BreadcrumbNav isArticle listicleCurrentPage={page} />}
                    {(article?.oAdvanceFeature?.bEditorsPick || article?.oAdvanceFeature?.bExclusiveArticle) && (
                      <div className={`${styles.advanceFeature} d-flex mb-2 mb-md-0 ms-md-auto me-1`}>
                        {article?.oAdvanceFeature?.bEditorsPick && (
                          <span className={`badge bg-light" ${styles.advFeature} d-flex align-items-center text-capitalize`}>
                            <EditorsPickIcon />
                            {t('common:EditorsPick')}
                          </span>
                        )}
                        {article?.oAdvanceFeature?.bExclusiveArticle && (
                          <span className={`badge bg-light" ${styles.advFeature} d-flex align-items-center text-capitalize ms-2`}>
                            <ExclusiveIcon />
                            {t('common:ExclusiveArticle')}
                          </span>
                        )}
                      </div>
                    )}
                    {article?.iEventId && (
                      <p className={`text-danger ${styles.liveUpdate} d-inline-block mb-0`}>
                        <span className={`${styles.liveIcon} d-inline-block position-relative rounded-circle`} />
                        {t('common:Live')}
                      </p>
                    )}
                  </div>
                  <article>
                    <h1 className={`${styles.title}`}>{article?.sTitle}</h1>
                    <h2 className={`${styles.subTitle} small-head mb-2 mb-md-3 pt-2 pt-md-3`}>{article?.sSubtitle}</h2>
                    <div className={`${styles.authorBlock} d-flex flex-wrap align-items-start mb-2 mb-md-3`}>
                      <div className="font-semi">
                        <div className={`${styles.authorDesc} pe-2 my-1 mb-md-2 mt-md-0`}>
                          <CustomLink
                            href={allRoutes.authorDetail(article?.oDisplayAuthor?.oSeo?.sSlug)}
                            className={`text-capitalize ${isGlanceView || isMobileWebView ? 'pe-none' : ''}`}
                            prefetch={false}
                          >
                            <>
                              <span className="text-muted">{t('common:By')} </span>
                              {article?.oDisplayAuthor?.sDisplayName} {/* <OnMouseAndScroll> */}
                              {article?.oDisplayAuthor?.bIsVerified && (
                                <span className={`${styles.verfied} d-inline-block rounded-circle align-text-top`}>
                                  <MyImage src={verified} priority={false} height={18} width={18} alt="verified" />
                                </span>
                              )}
                              {/* </OnMouseAndScroll> */}
                            </>
                          </CustomLink>
                          {/* <p className="mb-0 mt-1 xsmall-text text-secondary">{getDesignation(article?.oDisplayAuthor?.eDesignation)}</p> */}
                        </div>
                        {seoData?.eSchemaType !== 'ar' && (
                          <p className={`${styles.dates} w-100 xsmall-text font-semi mb-0`}>
                            {(article?.dPublishDisplayDate || article?.dPublishDate) && (
                              <time
                                className="op-published d-none d-md-inline-block"
                                dateTime={dateCheck(article?.dPublishDisplayDate || article?.dPublishDate)?.toISOString()}
                                suppressHydrationWarning
                              >
                                {t('common:Published')} -{' '}
                                {convertDt24hFormat(dateCheck(article?.dPublishDisplayDate || article?.dPublishDate))}
                              </time>
                            )}
                            {/* <time
                              className="op-modified"
                              dateTime={
                                (article?.dModifiedDate || article?.dUpdated) &&
                                dateCheck(article?.dModifiedDate || article?.dUpdated).toISOString()
                              }
                              suppressHydrationWarning
                            >
                              {t('common:Updated')} - {convertDt24hFormat(dateCheck(article?.dModifiedDate || article?.dUpdated))}
                            </time> */}
                          </p>
                        )}
                      </div>
                      {/* <Button className="theme-btn outline-btn xsmall-btn">
                        <Trans i18nKey="common:Follow" />
                      </Button> */}
                      <div className="ms-auto">
                        {/* <p className={`${styles.views} d-flex align-items-center mb-1`}>
                          <span className={`${styles.iconOuter} d-inline-block align-middle`}>
                            <ViewIcon />
                          </span>{' '}
                          {t('common:View')} : {abbreviateNumber(article?.nViewCount || article?.nOViews)}
                        </p> */}
                        <p className={`${styles.views} ${styles.duration} mb-0 d-flex align-items-center`}>
                          <span className={`${styles.iconOuter} d-inline-block align-middle`}>
                            <ClockIcon />
                          </span>
                          {article?.nDuration > 0 ? article?.nDuration : DEFAULT_BLOG_READ} {t('common:Minute')} {t('common:Read')}
                        </p>
                      </div>
                    </div>

                    {page <= 1 && article?.oImg?.sUrl && (
                      <figure className="op-tracker mb-0 order-2 order-md-1">
                        <div className={`${styles.postImg} position-relative overflow-hidden br-lg`}>
                          {/* <div className='ratio ratio-16x9'> */}
                          <ResponsiveImg
                            srcSet={mediaSet.current}
                            src={mainURL}
                            height="160"
                            width="256"
                            loading="eager"
                            fetchpriority="high"
                            // className="w-100"
                            alt={article?.oImg?.sText || article?.sSrtTitle}
                          />

                          {/* <Image
                            unoptimized
                            // fetchpriority="high"
                            src="https://media.crictracker.com/media/attachments/1743662967412_LSGvsMI.jpeg"
                            // sizes="(max-width: 767px) 480px, (min-width: 768px) 960px"
                            alt={article?.oImg?.sText || article?.sSrtTitle}
                            onError={(e) => {
                              console.log('error')
                              // typeof onError === 'function' && onError(e)
                            }}
                            // placeholder="blur"
                            // blurDataURL={BLUR_DATA_URL_BASE64}
                            // loading="eager"
                            height="160"
                            width="256"
                            // sizes="(max-width: 768px) 200px, 540px"
                          /> */}
                          {/* </div> */}
                          {article?.oImg?.sCaption && (
                            <div className={`${styles.captionBlock} position-absolute`}>
                              <div className={`${styles.icon} light-bg position-absolute start-0 bottom-0 rounded-circle overflow-hidden`}>
                                <MyImage isLoadOnInteraction={!isMobileWebView} src={infoIcon} alt="info" height="24" width="24" />
                              </div>
                              <div
                                className={`${styles.caption} light-bg py-1 ps-4 pe-2 mw-100 xsmall-text d-inline-block br-md c-transition`}
                                dangerouslySetInnerHTML={{ __html: article?.oImg?.sCaption }}
                              />
                            </div>
                          )}
                        </div>
                      </figure>
                    )}

                    <div className={`${styles.articleActionsMob} light-bg position-sticky text-center`}>
                      {!isPreviewMode && (
                        <ArticleActions
                          article={article}
                          type={type}
                          seoData={seoData}
                          currentClap={currentClap}
                          setCurrentClap={setCurrentClap}
                          totalClaps={totalClaps}
                          commentCount={commentCount}
                          setCommentCount={setCommentCount}
                          showComments={showComments}
                          setShowComments={setShowComments}
                          handleAddClap={handleAddClap}
                        />
                      )}
                    </div>
                    {isGlanceView && (
                      <GlanceAd
                        id="div-gpt-ad-2"
                        adId="Crictracker_small_banner_top"
                        dimension={[
                          [320, 100],
                          [320, 50],
                          [300, 50]
                        ]}
                        adUnitName="Crictracker_English_InArticleMedium_Mid1"
                        placementName="InArticleMedium"
                        className="d-flex justify-content-center"
                        width={300}
                        height={250}
                        pageName="crictracker.com"
                      />
                    )}
                    {isMobileWebView && !isMobileWebViewV2 && (
                      <Ads
                        id="div-ad-gpt-23148549803-1731397772-0"
                        adIdDesktop="crictracker_new_mobile_webapp_ap_atf1_320x168"
                        slotId="23148549803"
                        dimensionDesktop={[320, 168]}
                        className="text-center"
                      />
                    )}
                    {!(isMobileWebView || isGlanceView) && (
                      <div className="d-flex d-md-none flex-column" style={{ height: '270px' }}>
                        {width < 768 && ( // Mobile Below article image
                          <Ads
                            id="div-ad-gpt-23148549803-1728886625-1"
                            adIdDesktop="Crictracker_New_Mobile_AP_MID1_300x250"
                            slotId="23148549803"
                            dimensionDesktop={[300, 250]}
                            className="text-center mb-3"
                          />
                        )}
                      </div>
                    )}
                    <Ads
                      id="div-ad-gpt-23148549803-1728886625-1-d"
                      adIdDesktop="Crictracker_New_Desktop_AP_MID_728x90"
                      slotId="23148549803"
                      hideOn="mobile"
                      dimensionDesktop={[728, 90]}
                      className={'text-center mb-4'}
                    />
                    <div className="for-tabool-fire" ref={observe} />
                    {children}
                    {isMobileWebView && !isMobileWebViewV2 && (
                      <Ads
                        id="div-ad-gpt-23148549803-1731397820-0"
                        adIdDesktop="crictracker_new_mobile_webapp_ap_btf_300x250"
                        slotId="23148549803"
                        dimensionDesktop={[300, 250]}
                        className="text-center"
                      />
                    )}
                    {((article?.oSeo?.eType === 'ar' && article?.oAdvanceFeature?.bAllowLike) ||
                      article?.oSeo?.eType === 'fa' ||
                      article?.oAdvanceFeature?.bAllowComments) && (
                      <LikeComment
                        article={article}
                        totalClaps={totalClaps}
                        commentCount={commentCount}
                        setShowComments={setShowComments}
                        handleAddClap={handleAddClap}
                      />
                    )}
                  </article>
                </div>

                {/* Desktop/Mobile above related Video and article  */}
                {isGlanceView && (
                  <GlanceAd
                    id="div-gpt-ad-3"
                    adId="Crictracker_mrec_bottom"
                    dimension={[[300, 250], [336, 280], 'fluid']}
                    className="mt-2 d-flex justify-content-center"
                    adUnitName="Crictracker_English_InArticleMedium_Mid3"
                    placementName="InArticleMedium"
                    width={300}
                    height={250}
                    pageName="crictracker.com"
                  />
                )}
                {/* {article?.oSeo?.eType === 'ar' && (
                  <OnMouseAndScroll>
                    <VuuklePlugin article={article} id={article?._id} />
                  </OnMouseAndScroll>
                )} */}
                <div className={isMobileWebView ? 'mt-2' : ''}>
                  {/* {(isGlanceView) && inView && ( */}
                  {(isGlanceView || isMobileWebView) && inView && (
                    <RelatedArticle
                      categoryId={article?.oCategory?._id || article?.iCategoryId}
                      articleId={article?._id}
                      type={article?.oSeo?.eType}
                    />
                  )}
                  {!isGlanceView && !isPreviewMode && !isPreview && inView && (
                    <>
                      {/* {(width < 768) && <PixFuture />} */}
                      {width > 768 && (article?.oCategory?._id || article?.iCategoryId) && (
                        <ArticleRelatedVideos
                          categoryId={article?.oCategory?._id || article?.iCategoryId}
                          articleId={article?._id}
                          type={article?.oSeo?.eType}
                        />
                      )}
                      {!isMobileWebView && article?.oSeo?.eType === 'ar' && <ArticleStories />}
                      <ErrorBoundary>
                        <TaboolaAds />
                      </ErrorBoundary>
                    </>
                  )}
                </div>
              </div>
              <div className="col-xxl-3 col-lg-4 common-sidebar">
                {!isPreviewMode && width > 768 && (
                  <>
                    <CurrentSeries />
                    {article?.aPollId?.map((id) => (
                      <MultiPoll key={id} id={id} className="mb-3 mb-md-4" />
                    ))}
                    {article?.aQuiz?.map((q) => (
                      <MultiQuiz key={q?._id} id={q?._id} className="mb-3 mb-md-4" />
                    ))}
                    {/* {article?.oSeo?.eType === 'fa' && (
                      <CricketPedia />
                    )} */}
                    <OnMouseAndScroll>
                      {/* // For fantasy article and cricket match predictions category */}
                      {(type === 'fantasyArticle' || article?.oCategory?._id === '623184aef5d229bacb00ffb3') && (
                        <Ads
                          id="div-ad-gpt-23148549803-1728886625-1-r"
                          adIdDesktop="Crictracker_New_Desktop_AP_RightMID_300x600"
                          slotId="23148549803"
                          dimensionDesktop={[300, 600]}
                          className="mb-2"
                        />
                      )}
                      <AllWidget type={WIDGET.cricSpecial} show />
                      {(type === 'fantasyArticle' || article?.oCategory?._id === '623184aef5d229bacb00ffb3') && (
                        <Ads
                          id="div-ad-gpt-23148549803-1728886625-3-r"
                          adIdDesktop="Crictracker_New_Desktop_AP_RightMID_300x600"
                          slotId="23148549803"
                          dimensionDesktop={[300, 600]}
                          className="mb-2"
                        />
                      )}
                      <AllWidget type={WIDGET.trendingNews} show />
                      <TapMindSlider />
                      {/* <PixFuture /> */}
                      <Ads
                        id="div-ad-gpt-23148549803-1728886625-2"
                        adIdDesktop="Crictracker_New_Desktop_AP_RightMID_300x600"
                        slotId="23148549803"
                        dimensionDesktop={[300, 600]}
                        className="sticky-ads position-sticky mb-2"
                      />
                    </OnMouseAndScroll>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* For cricket interviews category */}
      {article?.oCategory?._id === '623184abf5d229bacb00feb4' && (
        <Script strategy="lazyOnload" src="https://news.google.com/swg/js/v1/swg-basic.js" onLoad={handleGoogleNews} />
      )}
    </Layout>
  )
}

ArticleDetail.propTypes = {
  article: PropTypes.object,
  seoData: PropTypes.object,
  isPreviewMode: PropTypes.bool,
  children: PropTypes.node.isRequired,
  type: PropTypes.string
}

export default ArticleDetail
